'use client'

import { type ReactNode, useState } from 'react'

import { cn } from '@/shared/lib/utils'
import { Button } from '@/shared/ui/Button'

const emphasisVariants = [
  'primary',
  'success',
  'destructive',
  'reward'
] as const

const softVariants = ['outline', 'secondary'] as const

function SampleGrid({
  title,
  description,
  className,
  children
}: {
  title: string
  description?: string
  className?: string
  children: ReactNode
}) {
  return (
    <section
      className={cn(
        'rounded-2xl border border-white/20 p-6 shadow-sm',
        className
      )}
    >
      <h2 className="text-lg font-bold text-[#1d1d2a]">{title}</h2>
      {description ? (
        <p className="mt-1 text-sm text-[#434368]/90">{description}</p>
      ) : null}
      <div className="mt-4 flex flex-col gap-4">{children}</div>
    </section>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-6">
      <span className="w-40 shrink-0 text-xs font-semibold uppercase tracking-wide text-[#434368]/80">
        {label}
      </span>
      <div className="flex flex-wrap items-center gap-3">{children}</div>
    </div>
  )
}

export function DevButtonsPage() {
  const [loadingDemo, setLoadingDemo] = useState(false)

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 pb-24">
      <header className="mb-10">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-jacaranda">
          Dev only
        </p>
        <h1 className="mt-2 font-sans text-3xl font-extrabold text-[#1d1d2a]">
          Button — matriz visual
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-[#434368]">
          Compare variantes, tamanhos, ícone, estados e fundos. Use para alinhar
          sombra, contraste e hierarquia com o modal de resgate.
        </p>
      </header>

      <SampleGrid
        title="Superfície clara (cards / fluxos)"
        description="Gradiente suave parecido com tiles do jogo."
        className="bg-[linear-gradient(180deg,#fbfbfb_0%,#e8ecff_100%)]"
      >
        {emphasisVariants.map((v) => (
          <Row key={v} label={v}>
            <Button variant={v} size="default">
              Default
            </Button>
            <Button variant={v} size="sm">
              Small
            </Button>
            <Button variant={v} size="default" icon aria-label={`${v} icon`}>
              →
            </Button>
            <Button variant={v} size="sm" icon aria-label={`${v} icon sm`}>
              →
            </Button>
          </Row>
        ))}
        {softVariants.map((v) => (
          <Row key={v} label={v}>
            <Button variant={v} size="default">
              Default
            </Button>
            <Button variant={v} size="sm">
              Small
            </Button>
          </Row>
        ))}
        <Row label="rounded + icon">
          {emphasisVariants.map((v) => (
            <Button
              key={v}
              variant={v}
              icon
              rounded
              aria-label={`${v} round`}
            >
              ✓
            </Button>
          ))}
        </Row>
      </SampleGrid>

      <SampleGrid
        title="Superfície escura (modal loot)"
        description="Par outline + reward como no CosmeticRedeemModal."
        className="mt-8 bg-[linear-gradient(180deg,#2d1b3d_0%,#160d1f_100%)] border-[#f4d03f]/40"
      >
        <Row label="pair">
          <Button variant="outline" size="default" className="min-w-[120px]">
            Voltar
          </Button>
          <Button variant="reward" size="default" className="min-w-[160px]">
            Confirmar resgate
          </Button>
        </Row>
        <Row label="reward sm (catálogo)">
          <div className="w-36">
            <Button variant="reward" size="sm" className="w-full">
              Resgatar
            </Button>
          </div>
        </Row>
      </SampleGrid>

      <SampleGrid
        title="Estados"
        description="Loading mantém cor do variant; disabled nos emphasis vira cinza."
        className="mt-8 bg-white"
      >
        <Row label="toggle loading (reward)">
          <Button
            variant="reward"
            loading={loadingDemo}
            onClick={() => setLoadingDemo((x) => !x)}
          >
            {loadingDemo ? 'Carregando…' : 'Clique para loading'}
          </Button>
        </Row>
        <Row label="disabled">
          <Button variant="reward" disabled>
            Reward off
          </Button>
          <Button variant="primary" disabled>
            Primary off
          </Button>
          <Button variant="outline" disabled>
            Outline off
          </Button>
        </Row>
      </SampleGrid>
    </div>
  )
}
